'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { AlertCard } from '@/components/AlertCard';
import { ConfirmActionDialog } from '@/components/Dialog/ConfirmActionDialog';
import { VStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import { toast } from '@/components/ui/use-toast';
import { accessRulesKeyPrefix, useAccessRuleList, useDeleteAccessRule } from '@/features/authentication-policy';
import { PolicySectionHeader } from '@/features/identity-policy';
import { handleApiError } from '@/lib/api/error';
import type { AuthenticationPolicyAccessRuleView } from '@/lib/api/orpc/contracts/authentication-policy-access-rule';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { AccessRulesTable } from './-components/AccessRulesTable';
export const Route = createFileRoute('/_staff/settings/authentication/_layout/access-rules/')({
  component: AccessRulesPage
});
function AccessRulesPage() {
  const {
    i18n,
    t
  } = useTranslation(defaultNS, {
    keyPrefix: accessRulesKeyPrefix
  });
  const {
    data,
    isPending,
    isError
  } = useAccessRuleList();
  const deleteMutation = useDeleteAccessRule();
  const [deleteTarget, setDeleteTarget] = useState<AuthenticationPolicyAccessRuleView | null>(null);
  if (isPending) return <ViewportLoader />;
  if (isError || data == null) {
    return <VStack className="w-full gap-4 px-12 py-6">
        <PolicySectionHeader iconName="ShieldIcon" title={t($ => $.title)} description={t($ => $.description)} />
        <AlertCard variant="mutedError" description={t($ => $.list.loadError)} />
      </VStack>;
  }
  const handleDelete = async () => {
    if (deleteTarget == null) return;
    try {
      await deleteMutation.mutateAsync({
        id: deleteTarget.id
      });
      setDeleteTarget(null);
      toast({
        description: t($ => $.toast.deleted)
      });
    } catch (error) {
      toast({
        description: handleApiError(error, {
          language: i18n.language
        }).message,
        variant: 'destructive'
      });
    }
  };
  return <VStack className="w-full gap-4 px-12 py-6">
      <PolicySectionHeader iconName="ShieldIcon" title={t($ => $.title)} description={t($ => $.description)} />

      <AccessRulesTable rows={data.items} isPending={false} onDelete={setDeleteTarget} />

      <ConfirmActionDialog isOpen={deleteTarget != null} isLoading={deleteMutation.isPending} title={t($ => $.delete.title)} description={t($ => $.delete.description)} okLabel={t($ => $.delete.confirm)} okVariant="danger" onClose={() => setDeleteTarget(null)} onConfirm={() => {
      void handleDelete();
    }} />
    </VStack>;
}